import type { APITokenResponse } from '../services/apis/privateAxiosService.ts';
import type { UserAccessTokenData } from './local-storage.ts';

export const convertAPIAuthTokenResponseToNormalShape = ({
  token_type,
  access_token,
  expires_in,
}: APITokenResponse): UserAccessTokenData => ({
  tokenType: token_type,
  accessToken: access_token,
  expiresIn: expires_in,
});

export type JwtMode = 'real' | 'demo';

const base64UrlDecodeToString = (input: string) => {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

  // atob expects "standard" base64
  const decoded = atob(padded);

  // handle UTF-8 payloads
  try {
    return decodeURIComponent(
      Array.from(decoded)
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );
  } catch {
    return decoded;
  }
};

export const getJwtModeFromAccessToken = (accessToken?: string | null): JwtMode | null => {
  if (!accessToken) return null;

  const parts = accessToken.split('.');
  if (parts.length < 2) return null;

  try {
    const payloadStr = base64UrlDecodeToString(parts[1]);
    const payload = JSON.parse(payloadStr) as { mode?: unknown };
    // If `mode` is absent, assume `real`.
    if (payload.mode === undefined) return 'real';
    
    const mode = typeof payload.mode === 'string' ? payload.mode.toLowerCase() : null;

    if (mode === 'demo' || mode === 'real') return mode;
    return 'real';
  } catch {
    return 'real';
  }
};

const parseJwtPayload = (accessToken: string): Record<string, unknown> | null => {
  const parts = accessToken.split('.');
  if (parts.length < 2) return null;

  try {
    const payloadStr = base64UrlDecodeToString(parts[1]);
    return JSON.parse(payloadStr) as Record<string, unknown>;
  } catch {
    return null;
  }
};

export const getJwtLanguageFromAccessToken = (
  accessToken?: string | null
): string | null => {
  if (!accessToken) return null;

  const payload = parseJwtPayload(accessToken);
  return typeof payload?.language === 'string' ? payload.language : null;
};

/** Returns true when the JWT `exp` claim is in the past (with a small clock-skew buffer). */
export const isAccessTokenExpired = (accessToken?: string | null): boolean => {
  if (!accessToken) return true;

  const payload = parseJwtPayload(accessToken);
  const exp = payload?.exp;

  if (typeof exp !== 'number') {
    return false;
  }

  const CLOCK_SKEW_SECONDS = 30;
  return Date.now() >= (exp - CLOCK_SKEW_SECONDS) * 1000;
};
